import { useQuery } from "@tanstack/react-query";
import { Check, GitCompare, Lock, Plus, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  InlineAlert,
  LoadingState,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type { BaselineSummary, ProjectDetail } from "../../shared/types/api";
import { BaselineCompareDrawer } from "./BaselineCompareDrawer";
import { CreateBaselineDrawer } from "./CreateBaselineDrawer";
import { CATEGORY_LABELS, formatHours } from "./projectVocabulary";

/**
 * Baseline versions (§25, §26, §27).
 *
 * <p>Every version stays readable exactly as it was signed. The panel makes that visible rather
 * than merely true: an approved baseline is marked as locked, and the only action offered against
 * it is comparison.
 */
export function ProjectBaselinesTab({
  project,
  onChanged,
}: {
  project: ProjectDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [creating, setCreating] = useState(false);
  const [comparing, setComparing] = useState<[BaselineSummary, BaselineSummary] | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const baselines = useQuery({
    queryKey: ["baselines", project.id],
    queryFn: () => api.get<BaselineSummary[]>(`/api/v1/projects/${project.id}/baselines`),
  });

  const act = async (path: string) => {
    setActionError(null);
    try {
      await api.post(path);
      void baselines.refetch();
      onChanged();
    } catch (error) {
      setActionError(error instanceof ApiError ? error.message : "Could not complete that.");
    }
  };

  if (baselines.isPending) {
    return <LoadingState label="Loading baselines…" />;
  }
  if (baselines.isError) {
    return <ErrorState error={baselines.error} onRetry={() => void baselines.refetch()} />;
  }

  const versions = baselines.data;
  const pending = versions.find(
    (baseline) => baseline.status === "DRAFT" || baseline.status === "PENDING_APPROVAL",
  );
  const approved = versions.filter((baseline) => baseline.status === "APPROVED");

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-ink-500">
          Each version is a snapshot of what was agreed, kept exactly as it was signed. Changing the
          commitment means approving a new version — the previous one is never edited.
        </p>
        <div className="flex gap-2">
          {approved.length >= 2 && (
            <Button
              variant="secondary"
              icon={<GitCompare className="h-4 w-4" />}
              onClick={() => setComparing([approved[approved.length - 1]!, approved[0]!])}
            >
              Compare
            </Button>
          )}
          {can("PROJECT_BASELINE_CREATE") && !pending && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
              {versions.length === 0 ? "Create baseline" : "Revise baseline"}
            </Button>
          )}
        </div>
      </div>

      {actionError && (
        <div className="mb-4">
          <InlineAlert tone="error">{actionError}</InlineAlert>
        </div>
      )}

      {versions.length === 0 ? (
        <Card>
          <EmptyState
            title="No baseline agreed yet"
            message="A baseline records what was committed: the approved hours, the dates and the scope. Until one exists there is nothing to measure the plan against."
            action={
              can("PROJECT_BASELINE_CREATE") && (
                <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
                  Create the first baseline
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {versions.map((baseline) => (
            <Card
              key={baseline.id}
              className={cn(baseline.current && "border-brand-300 ring-1 ring-brand-200")}
            >
              <CardHeader
                title={
                  <span className="flex items-center gap-2">
                    Version {baseline.versionNumber}
                    {baseline.current && <StatusBadge tone="blue">Current</StatusBadge>}
                    {baseline.status === "APPROVED" && !baseline.current && (
                      <StatusBadge tone="gray">Superseded</StatusBadge>
                    )}
                    {baseline.status !== "APPROVED" && (
                      <StatusBadge tone={baseline.status === "REJECTED" ? "red" : "amber"}>
                        {baseline.status === "REJECTED" ? "Rejected" : "Awaiting approval"}
                      </StatusBadge>
                    )}
                    {baseline.status === "APPROVED" && (
                      <span
                        className="flex items-center gap-1 text-xs text-ink-400"
                        title="An approved baseline can never be edited"
                      >
                        <Lock className="h-3 w-3" />
                        locked
                      </span>
                    )}
                  </span>
                }
                subtitle={
                  baseline.approvedByName
                    ? `Approved by ${baseline.approvedByName} on ${new Date(
                        baseline.approvedAt!,
                      ).toLocaleDateString()}`
                    : baseline.requestedByName
                      ? `Drafted by ${baseline.requestedByName}`
                      : undefined
                }
                action={
                  baseline.status !== "APPROVED" &&
                  baseline.status !== "REJECTED" &&
                  can("PROJECT_BASELINE_APPROVE") && (
                    <div className="flex gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        icon={<X className="h-3.5 w-3.5" />}
                        onClick={() =>
                          void act(
                            `/api/v1/projects/${project.id}/baselines/${baseline.id}/reject`,
                          )
                        }
                      >
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        icon={<Check className="h-3.5 w-3.5" />}
                        onClick={() =>
                          void act(
                            `/api/v1/projects/${project.id}/baselines/${baseline.id}/approve`,
                          )
                        }
                      >
                        Approve
                      </Button>
                    </div>
                  )
                }
              />
              <CardBody className="space-y-3">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <Figure label="Approved" value={formatHours(baseline.approvedHours)} />
                  <Figure label="Start" value={baseline.baselineStartDate} />
                  <Figure label="Finish" value={baseline.baselineEndDate} />
                  <Figure label="Manager" value={baseline.projectManagerName ?? "—"} />
                </div>

                {baseline.reason && (
                  <div className="rounded-lg border border-ink-200 bg-ink-50 px-3 py-2">
                    <p className="text-xs font-medium text-ink-500">Why this version exists</p>
                    <p className="mt-0.5 text-sm text-ink-800">{baseline.reason}</p>
                    {baseline.changeSummary && (
                      <p className="mt-1 text-sm text-ink-600">{baseline.changeSummary}</p>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() =>
                    setExpanded((current) => (current === baseline.id ? null : baseline.id))
                  }
                  className="text-sm font-medium text-brand-600 transition-colors hover:text-brand-700"
                >
                  {expanded === baseline.id ? "Hide" : "Show"} what was captured (
                  {baseline.items.length})
                </button>

                {expanded === baseline.id && (
                  <div className="overflow-hidden rounded-lg border border-ink-200">
                    {baseline.items.map((item, index) => (
                      <div
                        key={`${item.itemType}-${item.label}-${index}`}
                        className="flex items-center justify-between gap-3 border-b border-ink-100 px-3 py-2 text-sm last:border-0"
                      >
                        <span className="text-ink-500">
                          {item.itemType === "BUDGET_CATEGORY" && item.category
                            ? CATEGORY_LABELS[item.category]
                            : item.label}
                        </span>
                        <span className="tnum text-ink-900">
                          {item.estimateHours != null
                            ? formatHours(item.estimateHours)
                            : (item.targetDate ?? "—")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <CreateBaselineDrawer
        project={project}
        isRevision={versions.length > 0}
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => {
          void baselines.refetch();
          onChanged();
        }}
      />

      <BaselineCompareDrawer
        projectId={project.id}
        versions={approved}
        pair={comparing}
        onClose={() => setComparing(null)}
      />
    </>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-ink-500">{label}</p>
      <p className="tnum mt-0.5 text-sm font-medium text-ink-900">{value}</p>
    </div>
  );
}
